import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS, Block, Inline, Text } from "@contentful/rich-text-types";
import { Box, Icon, Image, Typography } from "@outschool/backpack";
import { ContentfulMagnetPageBodyLinks } from "@outschool/gql-backend-generated";
import { faQuoteLeft } from "@outschool/icons";
import lodashKebabCase from "lodash/kebabCase";
import React from "react";

function EmbeddedAsset({
  assetMap,
  node,
  isMobile
}: {
  assetMap: Map<string, any>;
  node: any;
  isMobile: boolean;
}) {
  const asset = assetMap.get(node.data.target.sys.id);

  const imageHeight = isMobile ? 250 : 350;
  const resize = asset.height > imageHeight;
  const resizeWidth = asset.width * (imageHeight / asset.height);
  return (
    <Box
      sx={{
        margin: "40px 0px",
        width: "100%"
      }}
    >
      <Box
        sx={{
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          width: resize ? resizeWidth : asset.width
        }}
      >
        <Image
          src={asset.url}
          alt={asset.description}
          width={resize ? resizeWidth : asset.width}
          height={resize ? imageHeight : asset.height}
        />
      </Box>
    </Box>
  );
}

export default function renderOptionsLinks(
  links: ContentfulMagnetPageBodyLinks,
  isMobile: boolean
): Options {
  const assetMap = new Map();
  for (const asset of links?.assets?.block) {
    if (asset) {
      assetMap.set(asset.sys.id, asset);
    }
  }
  return {
    renderNode: {
      [BLOCKS.QUOTE]: (_node, children) => (
        <blockquote>
          <Icon
            icon={faQuoteLeft}
            sx={{
              fontSize: "1.333em",
              color: "grey.700",
              float: "left",
              marginTop: "-25px"
            }}
          />
          {children}
        </blockquote>
      ),
      [BLOCKS.EMBEDDED_ASSET]: node => {
        return (
          <EmbeddedAsset assetMap={assetMap} node={node} isMobile={isMobile} />
        );
      }
    }
  };
}

function headingId(node: Block | Inline): string {
  const text = node.content
    .filter(c => c.nodeType === "text")
    .map(c => (c as Text).value)
    .join(" ");
  return lodashKebabCase(text);
}

export const headingOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <Typography variant="h1" id={headingId(node)}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Typography variant="h2" id={headingId(node)}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Typography variant="h3" id={headingId(node)}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Typography variant="h4" id={headingId(node)}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Typography variant="h5" id={headingId(node)}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <Typography variant="h6" id={headingId(node)}>
        {children}
      </Typography>
    )
  }
};
